import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import type { ProductDetailDto, ProductImageDto } from '@ecom/contracts';
import { useCatalogStore } from '@/stores/catalog.store';
import { adminApi, withSession } from '@/lib/api-client';
import { mediaUrl } from '@/lib/media';
import { fabricOfImage, fabricsOf } from '@/lib/fabrics';
import { useT } from '@/i18n/use-t';
import { GripIcon, ImageIcon, TrashIcon, UploadIcon } from '@/components/icons';
import { Section } from '@/components/page';
import { SortableGrid, SortableItem } from '@/components/sortable';
import { Alert, Button, Input, cx } from '@/components/ui';

/**
 * Product photographs.
 *
 * Dropped or picked several at a time, dragged into order — the first is the
 * one every listing card shows — and each linked to the colour it shows. The
 * link is what makes the storefront swap photographs when a shopper picks a
 * colour, and what a look's fabric picker recognises a fabric by; a photo
 * linked to nothing is one of the product's general ones.
 */
export function ImagesCard({
  product,
  onDraftChange,
}: {
  /** Set when editing a saved product. */
  product?: ProductDetailDto;
  /**
   * Set instead while creating one: the upload endpoint returns a storage key
   * without needing a product (ADR-013), so pictures are uploaded now and
   * attached the moment it exists.
   */
  onDraftChange?: (imageKeys: string[]) => void;
}) {
  const { saving, addImage, removeImage, reorderImages, updateImage } = useCatalogStore();
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);

  const [pending, setPending] = useState<{ key: string; url: string }[]>([]);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [over, setOver] = useState(false);

  const fabrics = useMemo(() => (product ? fabricsOf(product).filter((fabric) => fabric.color !== null) : []), [product]);

  const images: ProductImageDto[] = product
    ? [...product.images].sort((a, b) => a.sortOrder - b.sortOrder)
    : pending.map((entry, index) => ({ id: `pending-${index}`, url: entry.url, alt: null, sortOrder: index, variantId: null }));

  /**
   * Uploads run one after another, not all at once: ten multipart requests
   * racing one session refresh is how a count stops meaning anything.
   */
  const upload = async (files: File[]) => {
    const pictures = files.filter((file) => file.type.startsWith('image/'));
    if (pictures.length === 0) return;
    setBusy({ done: 0, total: pictures.length });
    setError(null);
    const uploaded: { key: string; url: string }[] = [];

    for (const [index, file] of pictures.entries()) {
      try {
        if (product) {
          if (!(await addImage(product.id, file))) setError(t('imageUploadFailed'));
        } else {
          const result = await withSession(() => adminApi.uploads.image(file));
          uploaded.push({ key: result.key, url: result.url });
        }
      } catch {
        // Named rather than swallowed: nine of ten uploaded and nothing said is
        // how a product ships missing a picture nobody noticed.
        setError(t('imageUploadFailed'));
      }
      setBusy({ done: index + 1, total: pictures.length });
    }

    if (uploaded.length > 0) {
      const next = [...pending, ...uploaded];
      setPending(next);
      onDraftChange?.(next.map((entry) => entry.key));
    }
    setBusy(null);
  };

  const reorder = (order: string[]) => {
    if (product) {
      void reorderImages(product.id, order);
      return;
    }
    // While creating, the ids are positional placeholders.
    const byId = new Map(images.map((image, position) => [image.id, position]));
    const next = order.map((id) => pending[byId.get(id) ?? 0]);
    setPending(next);
    onDraftChange?.(next.map((entry) => entry.key));
  };

  const remove = (image: ProductImageDto, index: number) => {
    if (product) {
      void removeImage(product.id, image.id);
      return;
    }
    const next = pending.filter((_, position) => position !== index);
    setPending(next);
    onDraftChange?.(next.map((entry) => entry.key));
  };

  const drop = {
    onDragOver: (event: DragEvent) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (event: DragEvent) => {
      if (!event.dataTransfer.files.length) return;
      event.preventDefault();
      setOver(false);
      void upload([...event.dataTransfer.files]);
    },
  };

  return (
    <Section
      title={t('mediaTitle')}
      subtitle={t('mediaHint')}
      action={
        <Button variant="secondary" size="sm" disabled={saving || busy !== null} onClick={() => fileInput.current?.click()}>
          <UploadIcon className="size-4" />
          {busy ? `${busy.done}/${busy.total}` : t('uploadImage')}
        </Button>
      }
    >
      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="sr-only"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length > 0) void upload(files);
          event.target.value = '';
        }}
      />

      {error ? (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div {...drop} className={cx('rounded-control transition-colors', over && 'bg-brand-50 ring-2 ring-brand-300')}>
        {images.length === 0 ? (
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed border-line bg-surface-sunken px-4 py-12 text-center transition-colors hover:border-brand-300"
          >
            <span className="inline-flex size-11 items-center justify-center rounded-full bg-surface text-ink-muted shadow-card">
              <ImageIcon className="size-5" />
            </span>
            <span className="text-sm font-medium text-ink">{t('dropImages')}</span>
            <span className="text-xs text-ink-muted">{t('noImages')}</span>
          </button>
        ) : (
          <SortableGrid
            items={images}
            disabled={saving || busy !== null}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5"
            onReorder={(next) => reorder(next.map((image) => image.id))}
          >
            {(image, index) => {
              const fabric = product ? fabricOfImage(fabrics, image) : null;
              return (
                <SortableItem key={image.id} id={image.id}>
                  {({ attributes, listeners }) => (
                    <div
                      className={cx(
                        'group flex h-full flex-col overflow-hidden rounded-control border bg-surface',
                        index === 0 ? 'border-brand-600' : 'border-line',
                      )}
                    >
                      <div className="relative aspect-square overflow-hidden bg-surface-sunken">
                        <button type="button" onClick={() => setViewing(index)} aria-label={t('previewImage')} className="absolute inset-0 block size-full">
                          <img src={mediaUrl(image.url)} alt={image.alt ?? ''} className="size-full object-cover" />
                        </button>
                        <span
                          {...attributes}
                          {...listeners}
                          aria-label={t('dragToReorder')}
                          className="absolute start-1.5 top-1.5 inline-flex size-7 cursor-grab items-center justify-center rounded-control bg-surface/90 text-ink-muted shadow-card active:cursor-grabbing"
                        >
                          <GripIcon className="size-4" />
                        </span>
                        <button
                          type="button"
                          onClick={() => remove(image, index)}
                          disabled={saving || busy !== null}
                          aria-label={t('remove')}
                          className="absolute end-1.5 top-1.5 inline-flex size-7 items-center justify-center rounded-control bg-surface/90 text-ink-muted opacity-0 shadow-card transition-opacity group-hover:opacity-100 hover:text-danger focus:opacity-100"
                        >
                          <TrashIcon className="size-4" />
                        </button>
                        {index === 0 ? (
                          <span className="absolute start-1.5 bottom-1.5 rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-medium text-brand-ink">
                            {t('mainImage')}
                          </span>
                        ) : null}
                        {fabric?.swatchHex ? (
                          <span
                            className="absolute end-1.5 bottom-1.5 size-5 rounded-full border-2 border-white shadow-card"
                            style={{ backgroundColor: fabric.swatchHex }}
                          />
                        ) : null}
                      </div>

                      {product && fabrics.length > 0 ? (
                        <select
                          aria-label={t('colour')}
                          value={fabric?.key ?? ''}
                          disabled={saving}
                          onChange={(event) => {
                            const target = fabrics.find((entry) => entry.key === event.target.value);
                            // Any variant of the colour will do: the storefront reads the colour off it.
                            void updateImage(image.id, { variantId: target?.variants[0]?.id ?? null });
                          }}
                          className="w-full border-t border-line bg-surface py-1.5 ps-2 pe-7 text-xs text-ink focus:outline-none"
                        >
                          <option value="">{t('imageGeneral')}</option>
                          {fabrics.map((entry) => (
                            <option key={entry.key} value={entry.key}>
                              {entry.label}
                            </option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  )}
                </SortableItem>
              );
            }}
          </SortableGrid>
        )}
      </div>

      {viewing !== null && images[viewing] ? (
        <Lightbox
          images={images}
          index={viewing}
          editable={Boolean(product)}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onAlt={(image, alt) => updateImage(image.id, { alt: alt || null })}
        />
      ) : null}
    </Section>
  );
}

/**
 * The full-size preview, and the place to describe a photograph. Escape
 * closes and the arrow keys move: this is where an operator checks twenty
 * pictures properly, and reaching for the mouse each time is what makes it
 * tedious.
 */
function Lightbox({
  images,
  index,
  editable,
  onIndex,
  onClose,
  onAlt,
}: {
  images: ProductImageDto[];
  index: number;
  editable: boolean;
  onIndex: (index: number) => void;
  onClose: () => void;
  onAlt: (image: ProductImageDto, alt: string) => Promise<boolean>;
}) {
  const t = useT();
  const { saving } = useCatalogStore();
  const image = images[index];
  const [alt, setAlt] = useState(image.alt ?? '');

  useEffect(() => setAlt(image.alt ?? ''), [image]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return;
      if (event.key === 'Escape') onClose();
      // Not mirrored for RTL: the arrows move through the list in its stored
      // order, which is the order the shopper will see.
      if (event.key === 'ArrowRight') onIndex(Math.min(index + 1, images.length - 1));
      if (event.key === 'ArrowLeft') onIndex(Math.max(index - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onIndex, onClose]);

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={t('previewImage')}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
    >
      <div className="flex max-h-full w-full max-w-4xl flex-col items-center gap-3" onClick={(event) => event.stopPropagation()}>
        <img src={mediaUrl(image.url)} alt={image.alt ?? ''} className="max-h-[65vh] rounded-card bg-white object-contain" />

        <div className="flex items-center gap-4 text-white">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onIndex(index - 1)}
            aria-label={t('moveEarlier')}
            className="rounded-control border border-white/40 px-3 py-1 disabled:opacity-30"
          >
            ‹
          </button>
          <span data-numeric className="text-sm">
            {index + 1} / {images.length}
          </span>
          <button
            type="button"
            disabled={index === images.length - 1}
            onClick={() => onIndex(index + 1)}
            aria-label={t('moveLater')}
            className="rounded-control border border-white/40 px-3 py-1 disabled:opacity-30"
          >
            ›
          </button>
        </div>

        {editable ? (
          <form
            className="flex w-full max-w-lg items-end gap-2 rounded-card bg-surface p-3"
            onSubmit={(event) => {
              event.preventDefault();
              void onAlt(image, alt.trim());
            }}
          >
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-ink">{t('altText')}</span>
              <Input value={alt} onChange={(event) => setAlt(event.target.value)} placeholder={t('altTextHint')} maxLength={300} />
            </label>
            <Button type="submit" disabled={saving || alt.trim() === (image.alt ?? '')}>
              {saving ? t('saving') : t('save')}
            </Button>
          </form>
        ) : null}

        <Button variant="secondary" onClick={onClose}>
          {t('close')}
        </Button>
      </div>
    </div>
  );
}
